
import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Box, Paper, Dialog, DialogTitle, DialogContent, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingSpinner } from '../../../utility';

const QualityPlanTimeline = ({ batch, currDarkThemeClass, timeLineData, isLoading, totalDays: propTotalDays, subphases = [], targetSubphaseId, targetSubphaseDate, actualStartDate }) => {
    const svgRef = useRef(null);
    const labelSvgRef = useRef(null);
    const containerRef = useRef(null);
    const popupChartRef = useRef(null);
    const [timelineData, setTimelineData] = useState({});
    const [collapsedFrtls, setCollapsedFrtls] = useState([]);
    const [popupData, setPopupData] = useState(null); // { day, sensorType, frtl, sRead, dateStr }
    const [viewMode, setViewMode] = useState('days'); // 'days' or 'weekly'
    const [expandedWeeks, setExpandedWeeks] = useState([]); // Indices of weeks expanded in weekly mode

    // Update local state when timeLineData prop changes
    useEffect(() => {
        setCollapsedFrtls([]);
        setExpandedWeeks([]);
        if (timeLineData && Object.keys(timeLineData).length > 0) {
            setTimelineData(timeLineData);
        }
    }, [timeLineData]);

    // Helper to get Date for a specific day number
    const getDateForDay = (dayNum) => {
        if (targetSubphaseId && targetSubphaseDate && subphases.length > 0) {
            const targetPhase = subphases.find(sp => sp.default_subphase_id == targetSubphaseId || sp.id == targetSubphaseId);
            if (targetPhase && (targetPhase.start_day > 0)) {
                const targetDateObj = new Date(targetSubphaseDate);
                const targetStartDay = targetPhase.start_day;
                const diffDays = dayNum - targetStartDay;
                const calculatedDate = new Date(targetDateObj);
                calculatedDate.setDate(targetDateObj.getDate() + diffDays);
                return calculatedDate;
            }
        }
        const rawStart = actualStartDate || batch?.actual_start_date || batch?.start_date;
        const start = new Date(rawStart || new Date());
        const current = new Date(start);
        current.setDate(start.getDate() + (dayNum - 1));
        return current;
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    };

    const getRangeSummaryData = (startDay, endDay, sensorType, frtl) => {
        let totalAvg = 0;
        let count = 0;
        let unit = null;
        let finalStatus = 'ok';
        let expDay = null;
        let expNight = null;
        let dayControl = null;
        let nightControl = null;
        let lowSum = 0; let lowCount = 0;
        let highSum = 0; let highCount = 0;

        for (let d = startDay; d <= endDay; d++) {
            const dayData = timelineData[String(d)];
            const fData = dayData?.frtls?.find(f => f.frtl === frtl);
            const sData = fData?.readings?.find(r => r.sensor_type && sensorType && r.sensor_type.toLowerCase() === sensorType.toLowerCase());

            if (sData) {
                if (sData.actual_day_avg !== null) {
                    totalAvg += sData.actual_day_avg;
                    count++;
                }
                if (sData.actual_low !== null) {
                    lowSum += sData.actual_low;
                    lowCount++;
                }
                if (sData.actual_high !== null) {
                    highSum += sData.actual_high;
                    highCount++;
                }
                unit = sData.unit;
                if (sData.status === 'alert') finalStatus = 'alert';
                else if (sData.status === 'warning' && finalStatus !== 'alert') finalStatus = 'warning';

                if (expDay === null || expDay === undefined) expDay = sData.expected_day;
                if (expNight === null || expNight === undefined) expNight = sData.expected_night;
                if (dayControl === null || dayControl === undefined) dayControl = sData.day_control;
                if (nightControl === null || nightControl === undefined) nightControl = sData.night_control;
            }
        }

        return {
            avg: count > 0 ? (totalAvg / count) : null,
            min: lowCount > 0 ? (lowSum / lowCount) : null,
            max: highCount > 0 ? (highSum / highCount) : null,
            unit,
            status: finalStatus,
            expected_day: expDay,
            expected_night: expNight,
            day_control: dayControl,
            night_control: nightControl,
        };
    };

    const formatControlLabel = (dayCtrl, nightCtrl) => {
        const hasD = dayCtrl !== null && dayCtrl !== undefined;
        const hasN = nightCtrl !== null && nightCtrl !== undefined;

        if (!hasD && !hasN) return '±-';
        if (dayCtrl === nightCtrl) return `±${Number(dayCtrl).toFixed(2).replace(/\.?0+$/, '')}`;
        return `±${hasD ? Number(dayCtrl).toFixed(2).replace(/\.?0+$/, '') : '-'}/±${hasN ? Number(nightCtrl).toFixed(2).replace(/\.?0+$/, '') : '-'}`;
    };

    // ===== POPUP LINE CHART =====
    useEffect(() => {
        if (!popupData) return;

        const renderChart = () => {
            if (!popupChartRef.current) return;
            d3.select(popupChartRef.current).selectAll('*').remove();

            const isDark = currDarkThemeClass === 'dark-theme';
            const { sRead } = popupData;
            const hourlyData = sRead?.hourly_data || [];
            const expMin = (sRead?.expected_day !== null && sRead?.expected_day !== undefined) ? parseFloat(sRead.expected_day) : null;
            const expMax = (sRead?.expected_night !== null && sRead?.expected_night !== undefined) ? parseFloat(sRead.expected_night) : expMin;

            const dayControlVal = sRead?.day_control ? parseFloat(sRead.day_control) : 0;
            const nightControlVal = sRead?.night_control ? parseFloat(sRead.night_control) : dayControlVal;

            const dayStartHour = sRead?.day_time ? parseInt(sRead.day_time.split(':')[0]) : 8;
            const nightStartHour = sRead?.night_time ? parseInt(sRead.night_time.split(':')[0]) : 20;

            const uControlDay = (expMax !== null) ? expMax + dayControlVal : null;
            const lControlDay = (expMin !== null) ? expMin - dayControlVal : null;
            const uControlNight = (expMax !== null) ? expMax + nightControlVal : null;
            const lControlNight = (expMin !== null) ? expMin - nightControlVal : null;

            // Get actual data points (only hours with data)
            const dataPoints = hourlyData.map(hd => ({
                hour: hd.hour,
                value: hd.value,
                high: hd.high,
                low: hd.low,
                status: hd.status
            }));

            // Chart dimensions
            const chartWidth = 1050;
            const chartHeight = 350;
            const margin = { top: 30, right: 100, bottom: 50, left: 60 };
            const innerWidth = chartWidth - margin.left - margin.right;
            const innerHeight = chartHeight - margin.top - margin.bottom;

            const svg = d3.select(popupChartRef.current)
                .attr('width', chartWidth)
                .attr('height', chartHeight);

            const chart = svg.append('g')
                .attr('transform', `translate(${margin.left}, ${margin.top})`);

            // Tooltip element
            const tooltip = d3.select('body').selectAll('.chart-tooltip').data([0]).join('div')
                .attr('class', 'chart-tooltip')
                .style('position', 'absolute')
                .style('visibility', 'hidden')
                .style('background', 'rgba(20, 20, 20, 0.95)')
                .style('color', '#fff')
                .style('padding', '10px')
                .style('border-radius', '4px')
                .style('border', '1px solid #444')
                .style('font-size', '12px')
                .style('pointer-events', 'none')
                .style('z-index', '9999')
                .style('box-shadow', '0 4px 12px rgba(0,0,0,0.5)');

            // X scale: 0-23 hours
            const xScale = d3.scaleLinear().domain([0, 23]).range([0, innerWidth]);

            // Y scale: auto-fit to data range + control lines
            const allValues = dataPoints.filter(d => d.value !== null).map(d => d.value);
            if (uControlDay !== null) allValues.push(uControlDay);
            if (lControlDay !== null) allValues.push(lControlDay);
            if (uControlNight !== null) allValues.push(uControlNight);
            if (lControlNight !== null) allValues.push(lControlNight);
            if (expMin !== null) allValues.push(expMin);
            if (expMax !== null) allValues.push(expMax);
            if (allValues.length === 0) allValues.push(0, 100);
            const yMin = d3.min(allValues) * 0.9;
            const yMax = d3.max(allValues) * 1.1;
            const yScale = d3.scaleLinear().domain([yMin, yMax]).range([innerHeight, 0]);

            // Draw grid lines
            chart.append('g')
                .attr('transform', `translate(0, ${innerHeight})`)
                .call(d3.axisBottom(xScale).ticks(24).tickFormat(d => `${String(d).padStart(2, '0')}:00`))
                .selectAll('text')
                .attr('fill', '#aaa')
                .attr('font-size', '9px')
                .attr('transform', 'rotate(-45)')
                .attr('text-anchor', 'end');

            chart.append('g')
                .call(d3.axisLeft(yScale).ticks(6))
                .selectAll('text')
                .attr('fill', '#aaa')
                .attr('font-size', '10px');

            // Style axis lines
            chart.selectAll('.domain').attr('stroke', isDark ? '#444' : '#bdc0b2');
            chart.selectAll('.tick line').attr('stroke', isDark ? '#333' : '#e6f1c1');


            // === Control Lines ===
            const drawControl = (val, label, isNight = false) => {
                if (val === null) return;
                chart.append('line')
                    .attr('x1', 0).attr('x2', innerWidth)
                    .attr('y1', yScale(val)).attr('y2', yScale(val))
                    .attr('stroke', isNight ? '#ff8888' : '#ff4444').attr('stroke-width', 1.5).attr('stroke-dasharray', '6,3');
                chart.append('text')
                    .attr('x', innerWidth + 5).attr('y', yScale(val) + 4)
                    .attr('fill', isNight ? '#ff8888' : '#ff4444').attr('font-size', '9px').text(label);
            };

            const controlsDiff = dayControlVal !== nightControlVal;

            if (uControlDay !== null) drawControl(uControlDay, `High Day: ${uControlDay.toFixed(2).replace(/\.?0+$/, '')}`);
            if (lControlDay !== null) drawControl(lControlDay, `Low Day: ${lControlDay.toFixed(2).replace(/\.?0+$/, '')}`);

            if (controlsDiff) {
                if (uControlNight !== null) drawControl(uControlNight, `High Night: ${uControlNight.toFixed(2).replace(/\.?0+$/, '')}`, true);
                if (lControlNight !== null) drawControl(lControlNight, `Low Night: ${lControlNight.toFixed(2).replace(/\.?0+$/, '')}`, true);
            }

            // === Expected Value Lines ===
            const drawExpected = (val, label) => {
                if (val === null) return;
                chart.append('line')
                    .attr('x1', 0).attr('x2', innerWidth)
                    .attr('y1', yScale(val)).attr('y2', yScale(val))
                    .attr('stroke', '#8888ff').attr('stroke-width', 1.5).attr('stroke-dasharray', '3,3');
                chart.append('text')
                    .attr('x', innerWidth + 5).attr('y', yScale(val) + 4)
                    .attr('fill', '#8888ff').attr('font-size', '9px').text(label);
            };

            if (expMin !== null) drawExpected(expMin, `Exp Min: ${expMin.toFixed(2).replace(/\.?0+$/, '')}`);
            if (expMax !== null && expMax !== expMin) drawExpected(expMax, `Exp Max: ${expMax.toFixed(2).replace(/\.?0+$/, '')}`);

            // === Actual Data Line ===
            const validPoints = dataPoints.filter(d => d.value !== null);
            if (validPoints.length > 0) {
                const getColor = (val, hour) => {
                    let uLimit = uControlDay;
                    let lLimit = lControlDay;

                    const isNight = nightStartHour > dayStartHour
                        ? (hour >= nightStartHour || hour < dayStartHour)
                        : (hour >= nightStartHour && hour < dayStartHour);

                    if (isNight) {
                        uLimit = uControlNight;
                        lLimit = lControlNight;
                    }

                    if (uLimit === null || lLimit === null) return '#44ff44';

                    // Crossing = Red
                    if (val > uLimit || val < lLimit) return '#ff4444';

                    // Touching = Yellow (using same 0.1 margin as transformer)
                    if (Math.abs(val - uLimit) <= 0.1 || Math.abs(val - lLimit) <= 0.1) return '#ffaa00';

                    return '#44ff44'; // Green: within band
                };

                // Draw the line segments with color coding
                for (let i = 0; i < validPoints.length - 1; i++) {
                    const p1 = validPoints[i];
                    const p2 = validPoints[i + 1];
                    const color = getColor(p1.value, p1.hour);
                    chart.append('line')
                        .attr('x1', xScale(p1.hour)).attr('y1', yScale(p1.value))
                        .attr('x2', xScale(p2.hour)).attr('y2', yScale(p2.value))
                        .attr('stroke', color).attr('stroke-width', 2.5);
                }

                // Draw data point dots and labels
                validPoints.forEach(p => {
                    const color = getColor(p.value, p.hour);
                    chart.append('circle')
                        .attr('cx', xScale(p.hour)).attr('cy', yScale(p.value))
                        .attr('r', 5).attr('fill', color).attr('stroke', '#fff').attr('stroke-width', 1)
                        .style('cursor', 'pointer')
                        .on('mouseover', function (event) {
                            d3.select(this).attr('r', 8).attr('stroke-width', 2);
                            tooltip.style('visibility', 'visible')
                                .html(`
                                    <div style="font-weight: bold; margin-bottom: 4px; border-bottom: 1px solid #444; padding-bottom: 4px;">Sensor Details</div>
                                    <div style="display: flex; justify-content: space-between; gap: 20px;"><span>Value:</span> <span style="color: ${color}; font-weight: bold;">${p.value.toFixed(2)}${sRead?.unit || ''}</span></div>
                                    <div style="display: flex; justify-content: space-between; gap: 20px;"><span>Time:</span> <span style="color: #aaa;">${String(p.hour).padStart(2, '0')}:00</span></div>
                                    <div style="display: flex; justify-content: space-between; gap: 20px;"><span>Date:</span> <span style="color: #aaa;">${popupData.dateStr}</span></div>
                                    <div style="display: flex; justify-content: space-between; gap: 20px;"><span>Sensor:</span> <span style="color: #aaa;">${popupData.sensorType}</span></div>
                                    <div style="display: flex; justify-content: space-between; gap: 20px;"><span>FRTL:</span> <span style="color: #aaa;">${popupData.frtl}</span></div>
                                `);
                        })
                        .on('mousemove', function (event) {
                            tooltip.style('top', (event.pageY - 10) + 'px')
                                .style('left', (event.pageX + 10) + 'px');
                        })
                        .on('mouseout', function () {
                            d3.select(this).attr('r', 5).attr('stroke-width', 1);
                            tooltip.style('visibility', 'hidden');
                        });
                    chart.append('text')
                        .attr('x', xScale(p.hour)).attr('y', yScale(p.value) - 10)
                        .attr('text-anchor', 'middle').attr('fill', isDark ? '#fff' : '#413E2E').attr('font-size', '10px').attr('font-weight', 'bold')
                        .text(p.value.toFixed(2).replace(/\.?0+$/, ''));
                });
            }

            // Legend
            const legendY = -15;
            const legendItems = [
                { color: '#44ff44', label: 'Within Control' },
                { color: '#ffaa00', label: 'ON Boundary' },
                { color: '#ff4444', label: 'Crossing Control' },
                { color: '#8888ff', label: 'Expected' },
            ];
            legendItems.forEach((item, i) => {
                const lx = i * 165;
                chart.append('rect').attr('x', lx).attr('y', legendY - 5).attr('width', 10).attr('height', 10).attr('fill', item.color).attr('rx', 2);
                chart.append('text').attr('x', lx + 14).attr('y', legendY + 4).attr('fill', isDark ? '#ccc' : '#413E2E').attr('font-size', '10px').text(item.label);
            });
        };

        // Delay to allow Dialog to mount the SVG element
        const timer = setTimeout(renderChart, 150);
        return () => clearTimeout(timer);
    }, [popupData]);

    // ===== MAIN TIMELINE =====
    useEffect(() => {
        if (!batch || !containerRef.current) return;

        const isDark = currDarkThemeClass === 'dark-theme';
        const bgActive = isDark ? '#1a1a1a' : '#f7faea';
        const bgSecondary = isDark ? '#2a2a2a' : '#e9ffc6';
        const textPrimary = isDark ? '#ffffff' : '#413E2E';
        const textSecondary = isDark ? '#aaaaaa' : '#6f745f';
        const borderColor = isDark ? '#444444' : '#bdc0b2';
        const gridLineColor = isDark ? '#333333' : '#e6f1c1';
        const neonPrimary = '#BDD959';
        const neonHeaderBg = isDark ? '#BDD95922' : '#BDD95944';

        const totalDays = propTotalDays || batch.totalDays || 365;

        d3.select(svgRef.current).selectAll('*').remove();
        d3.select(labelSvgRef.current).selectAll('*').remove();

        const dayWidth = 120;
        const labelWidth = 150;

        // --- Calculate Week Info ---
        const totalWeeks = Math.ceil(totalDays / 7);
        const weekInfo = [];
        let currentX = 0;

        for (let i = 0; i < totalWeeks; i++) {
            const startDay = i * 7 + 1;
            const endDay = Math.min((i + 1) * 7, totalDays);
            const isExpanded = viewMode === 'days' || expandedWeeks.includes(i);
            const numDays = endDay - startDay + 1;
            const width = isExpanded ? (numDays * dayWidth) : dayWidth;

            weekInfo.push({
                index: i,
                startDay,
                endDay,
                isExpanded,
                width,
                x: currentX
            });
            currentX += width;
        }

        const availableWidth = currentX;

        // --- Calculate Today's Day Number ---
        const todayDate = new Date();
        todayDate.setHours(0, 0, 0, 0);
        const timelineStartDate = getDateForDay(1);
        let todayDayNum = -1;
        if (timelineStartDate) {
            timelineStartDate.setHours(0, 0, 0, 0);
            const diffDays = Math.floor((todayDate - timelineStartDate) / (1000 * 60 * 60 * 24));
            todayDayNum = diffDays + 1;
        }

        const isToday = (day) => day === todayDayNum;

        // Subphases
        const subphaseHeight = 30;
        const sortedSubphases = [...subphases].sort((a, b) => a.start_day - b.start_day);
        const processedSubphases = sortedSubphases.map(phase => {
            const start = Math.max(1, phase.start_day);
            const end = Math.min(totalDays, phase.end_day || start);
            return { ...phase, laneIndex: 0, start, end };
        });
        const subphaseSectionHeight = subphaseHeight;

        // Layout
        const margin = { top: 20, right: 20, bottom: 20, left: 0 };
        const subphaseRowHeight = subphaseSectionHeight;
        const dayHeaderHeight = 40;
        const dateHeaderHeight = 40;
        const frtlHeaderHeight = 40;
        const sensorRowHeight = 80;

        const firstDayData = timelineData["1"] || Object.values(timelineData)[0];
        const frtls = firstDayData?.frtls || [];

        let totalDataHeight = 0;
        frtls.forEach(f => {
            const isCollapsed = collapsedFrtls.includes(f.frtl);
            const sensorCount = isCollapsed ? 0 : (f.readings?.length || 0);
            totalDataHeight += frtlHeaderHeight + (sensorRowHeight * sensorCount);
        });

        const height = margin.top + subphaseRowHeight + dayHeaderHeight + dateHeaderHeight + (totalDataHeight || 100) + margin.bottom;

        const svg = d3.select(svgRef.current).attr('width', availableWidth).attr('height', height);
        // Add full background rect
        svg.insert('rect', ':first-child').attr('width', availableWidth).attr('height', height).attr('fill', bgActive);

        const g = svg.append('g').attr('transform', `translate(0, ${margin.top})`);
        const labelSvg = d3.select(labelSvgRef.current).attr('width', labelWidth).attr('height', height);
        // Add full background rect
        labelSvg.insert('rect', ':first-child').attr('width', labelWidth).attr('height', height).attr('fill', bgActive);

        const getXForDay = (day) => {
            const weekIdx = Math.floor((day - 1) / 7);
            const week = weekInfo[weekIdx];
            if (!week) return 0;
            if (!week.isExpanded) return week.x;
            return week.x + (day - week.startDay) * dayWidth;
        };

        // Render Subphases
        if (processedSubphases && processedSubphases.length > 0) {
            processedSubphases.forEach((phase) => {
                if (phase.start > totalDays) return;
                const startX = getXForDay(phase.start);
                const endX = getXForDay(phase.end) + dayWidth;
                const width = Math.max(1, endX - startX);
                const yPos = phase.laneIndex * subphaseHeight;

                g.append('rect').attr('x', startX).attr('y', yPos).attr('width', width).attr('height', subphaseHeight)
                    .attr('fill', isDark ? '#444' : '#D6E797').attr('stroke', borderColor).attr('stroke-width', 1)
                    .append('title').text(`${phase.name} (Day ${phase.start} - ${phase.end})`);

                if (width > 15) {
                    g.append('text').attr('x', startX + width / 2).attr('y', yPos + subphaseHeight / 2)
                        .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
                        .attr('fill', isDark ? '#e0e0e0' : '#496019').attr('font-size', '10px').attr('font-weight', '500')
                        .style('pointer-events', 'none').text(phase.name)
                        .each(function () {
                            const self = d3.select(this);
                            const textLength = self.node().getComputedTextLength();
                            if (textLength > width - 4) {
                                const chars = Math.floor(width / 6);
                                if (chars > 3) self.text(phase.name.substring(0, chars - 1) + "..");
                                else self.text("");
                            }
                        });
                }
            });
        }

        // Today Column Highlight - Neon Borders
        if (todayDayNum >= 1 && todayDayNum <= totalDays) {
            const todayX = getXForDay(todayDayNum);
            // Draw neon dashed borders on both sides of today's column
            [todayX, todayX + dayWidth].forEach(xPos => {
                g.append('line')
                    .attr('x1', xPos).attr('y1', 0)
                    .attr('x2', xPos).attr('y2', height)
                    .attr('stroke', neonPrimary).attr('stroke-width', 2)
                    .attr('stroke-dasharray', '4,2')
                    .style('pointer-events', 'none');
            });
        }

        // Labels
        const labels = ['Subphase', 'Days', 'Date'];
        const labelHeights = [subphaseRowHeight, dayHeaderHeight, dateHeaderHeight];
        let labelY = margin.top;
        labels.forEach((label, i) => {
            labelSvg.append('rect').attr('x', 0).attr('y', labelY).attr('width', labelWidth).attr('height', labelHeights[i]).attr('fill', bgActive).attr('stroke', borderColor).attr('stroke-width', 2);
            labelSvg.append('text').attr('x', labelWidth / 2).attr('y', labelY + (labelHeights[i] / 2)).attr('text-anchor', 'middle').attr('dominant-baseline', 'middle').attr('fill', textPrimary).attr('font-size', '14px').attr('font-weight', 'bold').text(label);
            labelY += labelHeights[i];
        });

        // Day + Date Headers
        weekInfo.forEach(week => {
            if (week.isExpanded) {
                // Render individual days
                for (let day = week.startDay; day <= week.endDay; day++) {
                    const x = getXForDay(day);
                    const isTodayDay = isToday(day);
                    const highlightColor = isTodayDay ? neonHeaderBg : bgActive; // Neon background for today's headers
                    const textColor = isTodayDay ? neonPrimary : textPrimary; // Neon text for today

                    g.append('rect').attr('x', x).attr('y', subphaseRowHeight).attr('width', dayWidth).attr('height', dayHeaderHeight).attr('fill', highlightColor).attr('stroke', borderColor).attr('stroke-width', 1);
                    g.append('text').attr('x', x + dayWidth / 2).attr('y', subphaseRowHeight + dayHeaderHeight / 2).attr('text-anchor', 'middle').attr('dominant-baseline', 'middle').attr('fill', textColor).attr('font-size', '14px').attr('font-weight', isTodayDay ? '900' : 'bold').text(`Day ${day}`);

                    const date = getDateForDay(day);
                    const dStr = formatDate(date);
                    g.append('rect').attr('x', x).attr('y', subphaseRowHeight + dayHeaderHeight).attr('width', dayWidth).attr('height', dateHeaderHeight).attr('fill', highlightColor).attr('stroke', borderColor).attr('stroke-width', 1);
                    g.append('text').attr('x', x + dayWidth / 2).attr('y', subphaseRowHeight + dayHeaderHeight + dateHeaderHeight / 2).attr('text-anchor', 'middle').attr('dominant-baseline', 'middle').attr('fill', isTodayDay ? textColor : textSecondary).attr('font-size', '12px').attr('font-weight', isTodayDay ? 'bold' : 'normal').text(dStr);
                }
            } else {
                // Render collapsed week header
                const x = week.x;
                const headerRect = g.append('rect').attr('x', x).attr('y', subphaseRowHeight).attr('width', week.width).attr('height', dayHeaderHeight).attr('fill', bgSecondary).attr('stroke', borderColor).attr('stroke-width', 1).style('cursor', 'pointer').on('click', () => {
                    setExpandedWeeks(prev => [...prev, week.index]);
                });
                g.append('text').attr('x', x + week.width / 2).attr('y', subphaseRowHeight + dayHeaderHeight / 2).attr('text-anchor', 'middle').attr('dominant-baseline', 'middle').attr('fill', textPrimary).attr('font-size', '12px').attr('font-weight', 'bold').style('pointer-events', 'none').text(`Day ${week.startDay}-${week.endDay}`);

                const dateRangeStr = `${formatDate(getDateForDay(week.startDay))} - ${formatDate(getDateForDay(week.endDay))}`;
                g.append('rect').attr('x', x).attr('y', subphaseRowHeight + dayHeaderHeight).attr('width', week.width).attr('height', dateHeaderHeight).attr('fill', bgActive).attr('stroke', borderColor).attr('stroke-width', 1);
                g.append('text').attr('x', x + week.width / 2).attr('y', subphaseRowHeight + dayHeaderHeight + dateHeaderHeight / 2)
                    .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
                    .attr('fill', textSecondary).attr('font-size', '10px').text(dateRangeStr);
            }

            // In weekly mode, allow clicking individual day headers to collapse? 
            // The user said "days will be collapseable". 
            // I'll make the expanded week headers clickable to collapse back.
            if (viewMode === 'weekly' && week.isExpanded) {
                g.append('rect')
                    .attr('x', week.x)
                    .attr('y', subphaseRowHeight)
                    .attr('width', week.width)
                    .attr('height', dayHeaderHeight)
                    .attr('fill', 'transparent')
                    .style('cursor', 'pointer')
                    .on('click', () => {
                        setExpandedWeeks(prev => prev.filter(w => w !== week.index));
                    });
            }
        });

        // Data Rows
        let rowY = subphaseRowHeight + dayHeaderHeight + dateHeaderHeight;
        if (frtls.length === 0) {
            g.append('text').attr('x', availableWidth / 2).attr('y', rowY + 50).attr('text-anchor', 'middle').attr('fill', textSecondary).text('No sensor data available');
        } else {
            frtls.forEach((frtlData) => {
                const isCollapsed = collapsedFrtls.includes(frtlData.frtl);
                const fHL = labelSvg.append('g').style('cursor', 'pointer').on('click', () => setCollapsedFrtls(prev => prev.includes(frtlData.frtl) ? prev.filter(f => f !== frtlData.frtl) : [...prev, frtlData.frtl]));
                fHL.append('rect').attr('x', 0).attr('y', margin.top + rowY).attr('width', labelWidth).attr('height', frtlHeaderHeight).attr('fill', bgSecondary).attr('stroke', borderColor).attr('stroke-width', 1);
                fHL.append('text').attr('x', 10).attr('y', margin.top + rowY + frtlHeaderHeight / 2).attr('text-anchor', 'start').attr('dominant-baseline', 'middle').attr('fill', textPrimary).attr('font-size', '12px').attr('font-weight', 'bold').text(`${isCollapsed ? '▶' : '▼'} FRTL: ${frtlData.frtl}`);
                const fH = svg.append('g');
                fH.append('rect').attr('x', 0).attr('y', margin.top + rowY).attr('width', availableWidth).attr('height', frtlHeaderHeight).attr('fill', bgSecondary).attr('stroke', borderColor).attr('stroke-width', 1);
                fH.append('text').attr('x', 10).attr('y', margin.top + rowY + frtlHeaderHeight / 2).attr('text-anchor', 'start').attr('dominant-baseline', 'middle').attr('fill', textPrimary).attr('font-size', '12px').text(`Room: ${frtlData.room_name}`);
                rowY += frtlHeaderHeight;

                if (!isCollapsed) {
                    (frtlData.readings || []).forEach((reading) => {
                        const sType = reading.sensor_type;
                        labelSvg.append('rect').attr('x', 0).attr('y', margin.top + rowY).attr('width', labelWidth).attr('height', sensorRowHeight).attr('fill', bgActive).attr('stroke', borderColor).attr('stroke-width', 1);
                        labelSvg.append('text').attr('x', labelWidth / 2).attr('y', margin.top + rowY + sensorRowHeight / 2).attr('text-anchor', 'middle').attr('dominant-baseline', 'middle').attr('fill', textPrimary).attr('font-size', '12px').attr('font-weight', 'bold').text(sType);

                        weekInfo.forEach(week => {
                            if (week.isExpanded) {
                                for (let day = week.startDay; day <= week.endDay; day++) {
                                    const x = getXForDay(day);
                                    const dData = timelineData[String(day)];
                                    const fData = dData?.frtls?.find(f => f.frtl === frtlData.frtl);
                                    const sRead = fData?.readings?.find(r => r.sensor_type && sType && r.sensor_type.toLowerCase() === sType.toLowerCase());

                                    const isTodayDayCell = isToday(day);
                                    let fill = isDark ? '#0a0a0a' : '#ffffff';
                                    if (sRead) {
                                        if (sRead.actual_day_avg === null || sRead.status === 'pending') fill = isDark ? '#161616' : '#ffffff';
                                        else if (sRead.status === 'alert') fill = isDark ? '#2a1212' : '#ffebee';
                                        else if (sRead.status === 'warning') fill = isDark ? '#2a2212' : '#fff9c4';
                                        else if (sRead.status === 'ok') fill = isDark ? '#122012' : '#e8f5e9';
                                        else fill = isDark ? '#161616' : '#ffffff';
                                    }

                                    g.append('rect').attr('x', x).attr('y', rowY).attr('width', dayWidth).attr('height', sensorRowHeight)
                                        .attr('fill', fill).attr('stroke', borderColor).attr('stroke-width', 0.5)
                                        .style('cursor', 'pointer')
                                        .on('click', () => {
                                            const dateStr = formatDate(getDateForDay(day));
                                            setPopupData({ day, sensorType: sType, frtl: frtlData.frtl, sRead, dateStr });
                                        });

                                    if (sRead) {
                                        const isTodayCell = isToday(day);
                                        const expLabel = `Exp: ${Number(sRead.expected_day ?? 0).toFixed(2).replace(/\.?0+$/, '')}/${Number(sRead.expected_night ?? 0).toFixed(2).replace(/\.?0+$/, '')}`;
                                        g.append('text').attr('x', x + 5).attr('y', rowY + 20).attr('text-anchor', 'start').attr('fill', isTodayCell ? neonPrimary : textSecondary).attr('font-size', '10px').style('pointer-events', 'none').text(expLabel);
                                        const ctrlText = formatControlLabel(sRead.day_control, sRead.night_control);
                                        g.append('text').attr('x', x + 5).attr('y', rowY + 32).attr('text-anchor', 'start').attr('fill', isTodayCell ? (isDark ? '#BDD959aa' : '#BDD959') : textSecondary).attr('font-size', '9px').style('pointer-events', 'none').text(`(${ctrlText})`);
                                        if (sRead.actual_day_avg !== null) {
                                            g.append('line').attr('x1', x).attr('y1', rowY + sensorRowHeight).attr('x2', x + dayWidth).attr('y2', rowY).attr('stroke', borderColor).attr('stroke-width', 1).style('pointer-events', 'none');
                                            const actLabel = `Act: ${Number(sRead.actual_low ?? 0).toFixed(1).replace(/\.?0+$/, '')}/${Number(sRead.actual_high ?? 0).toFixed(1).replace(/\.?0+$/, '')}`;
                                            g.append('text').attr('x', x + dayWidth - 5).attr('y', rowY + sensorRowHeight - 10).attr('text-anchor', 'end').attr('fill', textPrimary).attr('font-size', '11px').attr('font-weight', 'bold').style('pointer-events', 'none').text(actLabel);
                                            g.append('text').attr('x', x + 5).attr('y', rowY + sensorRowHeight - 10).attr('text-anchor', 'start').attr('fill', textSecondary).attr('font-size', '9px').style('pointer-events', 'none').text(sRead.unit);
                                            const c = sRead.status === 'alert' ? '#ff4444' : sRead.status === 'warning' ? '#ffaa00' : '#44ff44';
                                            g.append('circle').attr('cx', x + dayWidth - 8).attr('cy', rowY + 8).attr('r', 4).attr('fill', c).style('pointer-events', 'none');
                                        }
                                    }
                                }
                            } else {
                                // Render summary cell
                                const summary = getRangeSummaryData(week.startDay, week.endDay, sType, frtlData.frtl);
                                const x = week.x;

                                const isTodayInWeek = todayDayNum >= week.startDay && todayDayNum <= week.endDay;
                                let fill = isDark ? '#0a0a0a' : '#ffffff';
                                if (summary.avg === null || summary.status === 'pending') fill = isDark ? '#161616' : '#ffffff';
                                else if (summary.status === 'alert') fill = isDark ? '#2a1212' : '#ffebee';
                                else if (summary.status === 'warning') fill = isDark ? '#2a2212' : '#fff9c4';
                                else if (summary.status === 'ok') fill = isDark ? '#122012' : '#e8f5e9';

                                g.append('rect').attr('x', x).attr('y', rowY).attr('width', week.width).attr('height', sensorRowHeight)
                                    .attr('fill', fill).attr('stroke', borderColor).attr('stroke-width', 1);

                                if (summary.expected_day !== null || summary.expected_night !== null) {
                                    const isTodaySummary = todayDayNum >= week.startDay && todayDayNum <= week.endDay;
                                    g.append('text').attr('x', x + 5).attr('y', rowY + 20).attr('text-anchor', 'start').attr('fill', isTodaySummary ? neonPrimary : textSecondary).attr('font-size', '9px').text(`Exp: ${summary.expected_day ?? '-'}/${summary.expected_night ?? '-'}`);
                                    const ctrlTxt = formatControlLabel(summary.day_control, summary.night_control);
                                    g.append('text').attr('x', x + 5).attr('y', rowY + 32).attr('text-anchor', 'start').attr('fill', isTodaySummary ? (isDark ? '#BDD959aa' : neonPrimary) : textSecondary).attr('font-size', '8px').text(`(${ctrlTxt})`);
                                }

                                if (summary.avg !== null) {
                                    g.append('line').attr('x1', x).attr('y1', rowY + sensorRowHeight).attr('x2', x + week.width).attr('y2', rowY).attr('stroke', borderColor).attr('stroke-width', 1);
                                    const sumLabel = `Act: ${Number(summary.min ?? 0).toFixed(1).replace(/\.?0+$/, '')}/${Number(summary.max ?? 0).toFixed(1).replace(/\.?0+$/, '')}`;
                                    g.append('text').attr('x', x + week.width - 5).attr('y', rowY + sensorRowHeight - 10).attr('text-anchor', 'end').attr('fill', textPrimary).attr('font-size', '10px').attr('font-weight', 'bold').text(sumLabel);

                                    const c = summary.status === 'alert' ? '#ff4444' : summary.status === 'warning' ? '#ffaa00' : '#44ff44';
                                    g.append('circle').attr('cx', x + week.width - 8).attr('cy', rowY + 8).attr('r', 4).attr('fill', c);
                                }
                            }
                        });
                        rowY += sensorRowHeight;
                    });
                }
            });
        }
    }, [batch, timelineData, collapsedFrtls, subphases, targetSubphaseId, targetSubphaseDate, actualStartDate, propTotalDays, viewMode, expandedWeeks, currDarkThemeClass]);

    const firstDayKey = Object.keys(timelineData)[0];
    const frtlsForCheck = timelineData[firstDayKey]?.frtls || [];
    const hasSensorData = frtlsForCheck.length > 0;

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <LoadingSpinner />
            </Box>
        );
    }

    return (
        <Box>
            {hasSensorData && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newVal) => newVal && setViewMode(newVal)}
                        sx={{
                            backgroundColor: currDarkThemeClass === 'dark-theme' ? '#1a1a1a' : '#e9ffc6',
                            border: `1px solid ${currDarkThemeClass === 'dark-theme' ? '#444' : '#bdc0b2'}`,
                            '& .MuiToggleButton-root': {
                                color: currDarkThemeClass === 'dark-theme' ? '#888' : '#6f745f',
                                border: 'none',
                                px: 3,
                                py: 0.5,
                                textTransform: 'none',
                                fontWeight: 'bold',
                                '&.Mui-selected': {
                                    color: currDarkThemeClass === 'dark-theme' ? '#fff' : '#413E2E',
                                    backgroundColor: currDarkThemeClass === 'dark-theme' ? '#333' : '#D6E797',
                                },
                            }
                        }}
                    >
                        <ToggleButton value="days">All Days</ToggleButton>
                        <ToggleButton value="weekly">Weekly View</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            <Paper className={`customScroll ${currDarkThemeClass}`} sx={{ backgroundColor: currDarkThemeClass === 'dark-theme' ? '#1a1a1a' : '#f7faea', overflow: 'hidden', mt: hasSensorData ? 0 : 2 }} ref={containerRef}>
                {hasSensorData ? (
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Box sx={{ flexShrink: 0, position: 'sticky', left: 0, zIndex: 10, backgroundColor: currDarkThemeClass === 'dark-theme' ? '#1a1a1a' : '#f7faea' }}>
                            <svg ref={labelSvgRef}></svg>
                        </Box>
                        <Box className={`customScroll ${currDarkThemeClass}`} sx={{ overflowX: 'auto', flex: 1 }}>
                            <svg ref={svgRef}></svg>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, color: currDarkThemeClass === 'dark-theme' ? '#888' : '#6f745f', fontStyle: 'italic' }}>
                        No sensor data available for this batch
                    </Box>
                )}
            </Paper>

            {/* Popup Line Graph Dialog */}
            <Dialog
                open={!!popupData}
                onClose={() => setPopupData(null)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: currDarkThemeClass === 'dark-theme' ? '#1e1e1e' : '#f7faea',
                        color: currDarkThemeClass === 'dark-theme' ? '#fff' : '#413E2E',
                        borderRadius: 2,
                        border: `1px solid ${currDarkThemeClass === 'dark-theme' ? '#444' : '#bdc0b2'}`,
                    }
                }}
            >
                {popupData && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                            <Box>
                                <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                    Day {popupData.day} — {popupData.sensorType}
                                </span>
                                <span style={{ color: currDarkThemeClass === 'dark-theme' ? '#aaa' : '#6f745f', fontSize: '13px', marginLeft: 12 }}>
                                    {popupData.dateStr} | FRTL: {popupData.frtl}
                                </span>
                            </Box>
                            <IconButton onClick={() => setPopupData(null)} sx={{ color: currDarkThemeClass === 'dark-theme' ? '#aaa' : '#6f745f' }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ pt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                <svg ref={popupChartRef}></svg>
                            </Box>
                            {popupData.sRead && (
                                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', mt: 1, color: currDarkThemeClass === 'dark-theme' ? '#aaa' : '#6f745f', fontSize: '12px' }}>
                                    <span>Expected: {Number(popupData.sRead.expected_day ?? 0).toFixed(2).replace(/\.?0+$/, '')}/{Number(popupData.sRead.expected_night ?? 0).toFixed(2).replace(/\.?0+$/, '')}</span>
                                    <span>
                                        Control: {formatControlLabel(popupData.sRead.day_control, popupData.sRead.night_control)}
                                    </span>
                                    <span>Act: {Number(popupData.sRead.actual_low ?? 0).toFixed(1).replace(/\.?0+$/, '')}/{Number(popupData.sRead.actual_high ?? 0).toFixed(1).replace(/\.?0+$/, '')}</span>
                                </Box>
                            )}
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default QualityPlanTimeline;
